import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Api } from "../api/baseApi";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";

const FooterOne = () => {
  const {i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [resultMessage, setResultMessage] = useState("");
  const assetBase = process.env.PUBLIC_URL || "";
  const getLocalizedPath = (lang, path = "") => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return normalizedPath === "/" ? `/${lang}` : `/${lang}${normalizedPath}`;
  };

  const moveHomeClick = (event, href) => {
    event.preventDefault();
    const homePath = getLocalizedPath(i18n.language, "/");
    if (location.pathname !== homePath) {
      navigate(homePath, { replace: false });
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Initial values for Formik
  const initialValues = {
    email: "",
    project: "Tamias",
  };

  // Yup validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("footerOne.errors.0") || "Invalid email")
      .required(t("footerOne.errors.1") || "Required"),
  });

  // Submit handler
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const { data } = await Api.post("/api/subscribe", values);
      if (data.status) {
        setResultMessage({ status: true, message: t('errorMessages.0') });
      }
      resetForm();
    } catch (error) {
      if (error) {
        setResultMessage({ status: false, message: t('errorMessages.1') });
      }
    }
  };

  useEffect(() => {
    if (resultMessage) {
      setTimeout(() => {
        setResultMessage("");
      }, 5000);
    }
  }, [resultMessage]);

  return (
    <>
      <footer className="footer-section">
        <div className="footer-content">
          <div className="container container-two">
            <div className="row gy-5">
              {/* Logo and text */}
              <div className="col-xl-3 col-sm-6">
                <div className="footer-widget">
                  <div className="footer-widget__logo">
                    <Link to={getLocalizedPath(i18n.language, "/")}>
                      <img
                        src={`${assetBase}/assets/images/logo/tamias-logo_light.png`}
                        alt="Logo"
                      />
                    </Link>
                  </div>
                  <p className="footer-widget__desc">
                    {t("footerOne.logoText")}
                  </p>
                </div>
              </div>

              {/* Quick Links */}
              <div className="col-xl-3 col-sm-6 col-xs-6 ps-xl-5">
                <div className="footer-widget">
                  <h5 className="footer-widget__title text-white">
                    {t("footerOne.quickLinks")}
                  </h5>
                  <ul className="footer-lists">
                    <li className="footer-lists__item">
                      <a
                        className="footer-lists__link"
                        href="#about"
                        onClick={(event) => moveHomeClick(event, "#about")}
                      >
                        {t("about")}
                      </a>
                    </li>
                    <li className="footer-lists__item">
                      <a
                        className="footer-lists__link"
                        href="#solutions"
                        onClick={(event) => moveHomeClick(event, "#solutions")}
                      >
                        {t("solutions")}
                      </a>
                    </li>
                    <li className="footer-lists__item">
                      <NavLink to={getLocalizedPath(i18n.language, "/blog")} className="footer-lists__link">
                        {t("blog")}
                      </NavLink>
                    </li>
                    <li className="footer-lists__item">
                      <NavLink to={getLocalizedPath(i18n.language, "/contact")} className="footer-lists__link">
                        {t("contact")}
                      </NavLink>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Subscribe Form */}
              <div className="col-xl-4 col-sm-6">
                <div className="footer-widget">
                  <h5 className="footer-widget__title text-white">
                    {t("footerOne.subscribeTitle")}
                  </h5>
                  <p className="footer-widget__desc">
                    {t("footerOne.subscribeDesc")}
                  </p>

                  {resultMessage && (
                    <div style={{color: resultMessage?.status ? "green" : "red"}} role="alert">
                      {resultMessage?.message}
                    </div>
                  )}
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form
                        autoComplete="off"
                        className="mt-4 subscribe-box d-flex  flex-column gap-2"
                      >
                        <Field
                          type="email"
                          name="email"
                          className="form-control common-input pill text-white footer-input"
                          placeholder={t("footerOne.subscribePlaceholder")}
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-danger small"
                        />

                        <button
                          id="subscribe-button"
                          type="submit"
                          className="btn btn-orange btn-lg w-100 pill"
                          disabled={isSubmitting}
                        >
                          {t("footerOne.subscribeBtn")}
                        </button>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom Footer */}
      <div className="bottom-footer">
        <div className="container container-two">
          <div className="bottom-footer__inner flx-between gap-3">
            <p className="bottom-footer__text font-14">
              {t("footerOne.copyright.0")}
            </p>
            <div className="footer-links">
              <Link
                to={"https://itresources.io/en/privacy-policy"}
                className="footer-link font-14"
              >
                {t("footerOne.copyright.2")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FooterOne;
